import { useCallback, useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";

import Logo from "../../../../assets/images/logo.png";
import MenuImage from "../../../../assets/images/menu-icon.svg";

import useStores from "../../../../hooks/useStores";
import useGoogleTagManager from "../../../../hooks/useGTM";
import { getQueryUrlParam, setQueryParams } from "../../../../utils";

import {
  HeaderContent,
  LogoButton,
  LogoImage,
  MenuButton,
  MenuIcon,
  MenuLink,
  MobileMenuContainer,
  Navbar,
  NavLink,
  StyledBox,
  StyledMenu,
  StyledMenuItem,
} from "../styles/HeaderStyles";

const NAV_ITEMS = [
  {
    label: "Página Inicial",
    to: "/#inicio",
    trackingName: "pagina_inicial",
  },
  {
    label: "Rede Credenciada",
    to: "/#rede",
    trackingName: "rede_credenciada",
  },
  {
    label: "Vantagens",
    to: "/#vantagens",
    trackingName: "vantagens",
  },
  {
    label: "Dúvidas?",
    to: "/#duvidas",
    trackingName: "duvidas",
  },
] as const;

const MOBILE_MEDIA_QUERY = "(max-width: 599px)";

const Header = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const { correctorAndProducerStore } = useStores();
  const { correctorData, setCorrector } = correctorAndProducerStore;

  const { interactionEvent } = useGoogleTagManager();

  const correctorFromQuery = useMemo(
    () => ({
      ev: getQueryUrlParam("ev", location.search),
      aa: getQueryUrlParam("aa", location.search),
      ea: getQueryUrlParam("ea", location.search),
      np: getQueryUrlParam("np", location.search),
      ep: getQueryUrlParam("ep", location.search),
    }),
    [location.search]
  );

  useEffect(() => {
    setCorrector(correctorFromQuery);
  }, [correctorFromQuery, setCorrector]);

  const trackHeaderClick = useCallback(
    (linkName: string) => {
      interactionEvent({
        event_action: "clicou",
        event_category: "header",
        event_label: linkName,
        event_name: `clicou_header_${linkName}`,
      });
    },
    [interactionEvent]
  );

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNavigate = useCallback(() => {
    const { ev, ea, aa, np, ep } = correctorData;

    const hasCorrectorParams = Boolean(ev || ea || aa || np || ep);

    if (hasCorrectorParams) {
      navigate(
        setQueryParams("/empresa", {
          ev,
          ea,
          aa,
          np,
          ep,
        })
      );
      return;
    }

    navigate("/");
  }, [correctorData, navigate]);

  const handleNavTracking = useCallback(
    (trackingName: string) => {
      trackHeaderClick(trackingName);
      handleMenuClose();
    },
    [handleMenuClose, trackHeaderClick]
  );

  return (
    <StyledBox id="inicio">
      <HeaderContent>
        <LogoButton
          type="button"
          onClick={handleNavigate}
          aria-label="Ir para a página inicial"
        >
          <LogoImage src={Logo} alt="SulAmérica" />
        </LogoButton>

        {!isMobile ? (
          <Navbar aria-label="Navegação principal">
            {NAV_ITEMS.map(({ label, to, trackingName }) => (
              <NavLink
                key={trackingName}
                smooth
                to={to}
                onClick={() => handleNavTracking(trackingName)}
              >
                {label}
              </NavLink>
            ))}
          </Navbar>
        ) : (
          <MobileMenuContainer>
            <MenuButton
              id="menu-button"
              aria-label="Abrir menu"
              aria-controls={open ? "menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleMenuOpen}
            >
              <MenuIcon src={MenuImage} alt="" aria-hidden="true" />
            </MenuButton>

            <StyledMenu
              id="menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              slotProps={{
                list: {
                  "aria-labelledby": "menu-button",
                }
              }}
            >
              {NAV_ITEMS.map(({ label, to, trackingName }) => (
                <StyledMenuItem key={trackingName} onClick={handleMenuClose}>
                  <MenuLink
                    smooth
                    to={to}
                    onClick={() => handleNavTracking(trackingName)}
                  >
                    {label}
                  </MenuLink>
                </StyledMenuItem>
              ))}
            </StyledMenu>
          </MobileMenuContainer>
        )}
      </HeaderContent>
    </StyledBox>
  );
});

export default Header;
